import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPhone, FaLinkedin, FaMapMarkerAlt, FaExclamationCircle } from 'react-icons/fa';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { contactAPI } from '../utils/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--space-3xl);
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: var(--space-lg);
  color: var(--secondary-900);
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: var(--secondary-600);
  max-width: 600px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  h3 {
    margin-bottom: var(--space-lg);
    color: var(--primary-600);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
  
  svg {
    color: var(--primary-600);
    font-size: 1.25rem;
  }
`;

const ContactForm = styled(motion.form)`
  background: white;
  padding: var(--space-2xl);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
`;

const FormGroup = styled.div`
  margin-bottom: var(--space-lg);
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: var(--space-sm);
  font-weight: 500;
  color: var(--secondary-700);
  
  ${props => props.$hasError && `
    color: var(--error-600);
  `}
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: var(--space-md);
  padding-right: ${props => props.$hasError ? '2.5rem' : 'var(--space-md)'};
  border: 2px solid ${props => {
    if (props.$hasError) return 'var(--error-500)';
    if (props.$isFocused) return 'var(--primary-600)';
    return 'var(--secondary-200)';
  }};
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: all var(--transition-normal);
  background-color: ${props => props.$hasError ? 'var(--error-50)' : 'white'};

  &:focus {
    border-color: ${props => props.$hasError ? 'var(--error-600)' : 'var(--primary-600)'};
    outline: none;
    box-shadow: ${props => props.$hasError
    ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
    : '0 0 0 3px rgba(37, 99, 235, 0.1)'};
  }

  @media (max-width: 768px) {
    min-height: 44px;
  }
`;

const ErrorIcon = styled(FaExclamationCircle)`
  position: absolute;
  right: var(--space-md);
  color: var(--error-500);
  font-size: 1rem;
  pointer-events: none;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: var(--space-md);
  border: 2px solid ${props => {
    if (props.$hasError) return 'var(--error-500)';
    if (props.$isFocused) return 'var(--primary-600)';
    return 'var(--secondary-200)';
  }};
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all var(--transition-normal);
  background-color: ${props => props.$hasError ? 'var(--error-50)' : 'white'};

  &:focus {
    border-color: ${props => props.$hasError ? 'var(--error-600)' : 'var(--primary-600)'};
    outline: none;
    box-shadow: ${props => props.$hasError
    ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
    : '0 0 0 3px rgba(37, 99, 235, 0.1)'};
  }

  @media (max-width: 768px) {
    min-height: 44px;
  }
`;

const ErrorMessage = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin-top: var(--space-xs);
  color: var(--error-600);
  font-size: 0.875rem;
  font-weight: 500;
  
  svg {
    font-size: 0.875rem;
    flex-shrink: 0;
  }
`;

const SubmitButton = styled.button`
  background: var(--primary-600);
  color: white;
  padding: var(--space-md) var(--space-2xl);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  width: 100%;

  &:hover {
    background: var(--primary-700);
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--secondary-400);
    cursor: not-allowed;
    transform: none;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: var(--primary-600);
  cursor: pointer;
  flex-shrink: 0;

  &:focus {
    outline: 2px solid var(--primary-300);
    outline-offset: 2px;
  }
`;

const CheckboxLabel = styled.label`
  color: var(--secondary-700);
  font-size: 0.95rem;
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
`;

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const serviceParam = searchParams.get('service');

  const getPrefilledMessage = useCallback((serviceName) => {
    if (!serviceName) return '';

    if (i18n.language === 'de') {
      return `Hallo,

ich interessiere mich für Ihr Service "${serviceName}" und würde gerne mehr Informationen erhalten.

Bitte kontaktieren Sie mich, um weitere Details zu besprechen.

Mit freundlichen Grüßen`;
    } else {
      return `Hello,

I am interested in your "${serviceName}" service and would like to receive more information.

Please contact me to discuss further details.

Best regards`;
    }
  }, [i18n.language]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: serviceParam ? getPrefilledMessage(serviceParam) : '',
    subscribeNewsletter: false
  });
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    company: false,
    message: false
  });
  const [focused, setFocused] = useState({
    name: false,
    email: false,
    company: false,
    message: false
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Update message when service parameter changes
  useEffect(() => {
    if (serviceParam) {
      const prefilledMessage = getPrefilledMessage(serviceParam);
      setFormData(prev => ({
        ...prev,
        message: prefilledMessage
      }));
    }
  }, [serviceParam, getPrefilledMessage]);

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = t('contact.nameRequired');
        } else if (value.trim().length < 2) {
          error = t('contact.nameMinLength');
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = t('contact.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = t('contact.emailInvalid');
        }
        break;
      case 'message':
        if (!value.trim()) {
          error = t('contact.messageRequired');
        } else if (value.trim().length < 10) {
          error = t('contact.messageMinLength');
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      message: validateField('message', formData.message)
    };
    setErrors(newErrors);
    return !newErrors.name && !newErrors.email && !newErrors.message;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate field if it's been touched or form has been submitted
    if (touched[name] || submitAttempted) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setFocused(prev => ({ ...prev, [name]: false }));

    // Validate on blur
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleFocus = (e) => {
    setFocused(prev => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    // Mark all required fields as touched
    setTouched({
      name: true,
      email: true,
      company: false,
      message: true
    });

    // Validate entire form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await contactAPI.sendMessage(formData);
      setSubmitMessage(t('contact.success'));
      setFormData({ name: '', email: '', company: '', message: '', subscribeNewsletter: false });
      setTouched({ name: false, email: false, company: false, message: false });
      setFocused({ name: false, email: false, company: false, message: false });
      setErrors({ name: '', email: '', message: '' });
      setSubmitAttempted(false);
    } catch (error) {
      setSubmitMessage(t('contact.error'));
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with our professional Scrum Master and Agile Coach. Ready to transform your organization? Let's discuss your agile transformation goals."
        keywords="Contact Scrum Master, Agile Coach Contact, Scrum Consulting, Agile Transformation Contact, Professional Services"
      />
      <Breadcrumbs />
      <Container>
        <Header>
          <Title>{t('contact.title')}</Title>
          <Subtitle>
            {t('contact.subtitle')}
          </Subtitle>
        </Header>

        <ContactGrid>
          <ContactInfo>
            <h3>{t('contact.contactInformation')}</h3>
            <ContactItem>
              <FaEnvelope aria-hidden="true" />
              <div>
                <strong>{t('common.email')}</strong><br />
                <a href="mailto:contact@scrumnx.com">contact@scrumnx.com</a>
              </div>
            </ContactItem>
            <ContactItem>
              <FaPhone aria-hidden="true" />
              <div>
                <strong>{t('common.phone')}</strong><br />
                <a href="tel:+4917630574191">+49 17630574191</a>
              </div>
            </ContactItem>
            <ContactItem>
              <FaLinkedin aria-hidden="true" />
              <div>
                <strong>LinkedIn</strong><br />
                <a href="https://www.linkedin.com/company/ipm-consulting-or" target="_blank" rel="noopener noreferrer">
                  linkedin.com/company/ipm-consulting-or
                </a>
              </div>
            </ContactItem>
            <ContactItem>
              <FaMapMarkerAlt aria-hidden="true" />
              <div>
                <strong>{t('common.location')}</strong><br />
                {t('contact.availableWorldwide')}
              </div>
            </ContactItem>
          </ContactInfo>

          <ContactForm
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--primary-600)' }}>
              {t('contact.sendUsMessage')}
            </h3>

            {submitMessage && (
              <div style={{
                color: 'var(--success-600)',
                marginBottom: 'var(--space-lg)',
                padding: 'var(--space-md)',
                background: 'var(--success-50)',
                borderRadius: 'var(--radius-md)'
              }}>
                {submitMessage}
              </div>
            )}

            <FormGroup>
              <Label htmlFor="name" $hasError={errors.name && (touched.name || submitAttempted)}>
                {t('contact.name')} *
              </Label>
              <InputWrapper>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  $hasError={errors.name && (touched.name || submitAttempted)}
                  $isFocused={focused.name}
                  aria-required="true"
                  aria-invalid={errors.name && (touched.name || submitAttempted) ? 'true' : 'false'}
                  aria-describedby={errors.name && (touched.name || submitAttempted) ? 'name-error' : undefined}
                />
                {errors.name && (touched.name || submitAttempted) && (
                  <ErrorIcon aria-hidden="true" />
                )}
              </InputWrapper>
              <AnimatePresence>
                {errors.name && (touched.name || submitAttempted) && (
                  <ErrorMessage
                    id="name-error"
                    role="alert"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaExclamationCircle />
                    <span>{errors.name}</span>
                  </ErrorMessage>
                )}
              </AnimatePresence>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email" $hasError={errors.email && (touched.email || submitAttempted)}>
                {t('contact.email')} *
              </Label>
              <InputWrapper>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  $hasError={errors.email && (touched.email || submitAttempted)}
                  $isFocused={focused.email}
                  aria-required="true"
                  aria-invalid={errors.email && (touched.email || submitAttempted) ? 'true' : 'false'}
                  aria-describedby={errors.email && (touched.email || submitAttempted) ? 'email-error' : undefined}
                />
                {errors.email && (touched.email || submitAttempted) && (
                  <ErrorIcon aria-hidden="true" />
                )}
              </InputWrapper>
              <AnimatePresence>
                {errors.email && (touched.email || submitAttempted) && (
                  <ErrorMessage
                    id="email-error"
                    role="alert"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaExclamationCircle />
                    <span>{errors.email}</span>
                  </ErrorMessage>
                )}
              </AnimatePresence>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="company">{t('contact.company')}</Label>
              <Input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                $hasError={false}
                $isFocused={focused.company}
                aria-describedby="company-help"
              />
              <div id="company-help" className="sr-only">Optional company name</div>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message" $hasError={errors.message && (touched.message || submitAttempted)}>
                {t('contact.message')} *
              </Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                placeholder={t('contact.messagePlaceholder')}
                $hasError={errors.message && (touched.message || submitAttempted)}
                $isFocused={focused.message}
                aria-required="true"
                aria-invalid={errors.message && (touched.message || submitAttempted) ? 'true' : 'false'}
                aria-describedby={errors.message && (touched.message || submitAttempted) ? 'message-error' : undefined}
              />
              <AnimatePresence>
                {errors.message && (touched.message || submitAttempted) && (
                  <ErrorMessage
                    id="message-error"
                    role="alert"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaExclamationCircle />
                    <span>{errors.message}</span>
                  </ErrorMessage>
                )}
              </AnimatePresence>
            </FormGroup>

            <FormGroup>
              <CheckboxWrapper>
                <Checkbox
                  type="checkbox"
                  id="subscribeNewsletter"
                  name="subscribeNewsletter"
                  checked={formData.subscribeNewsletter}
                  onChange={(e) => setFormData({ ...formData, subscribeNewsletter: e.target.checked })}
                />
                <CheckboxLabel htmlFor="subscribeNewsletter">
                  {t('contact.subscribeNewsletter', 'Subscribe to our newsletter for agile insights and updates')}
                </CheckboxLabel>
              </CheckboxWrapper>
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
            </SubmitButton>
          </ContactForm>
        </ContactGrid>
      </Container>
    </>
  );
};

export default ContactPage;